/**
 * WHAT:
 *   Yahoo Finance enrichment test component.
 *
 * WHY:
 *   Centralizes Yahoo Finance test UI for better maintainability.
 *
 * DOMAIN: Investments → Parqet → Test Tab → Components
 *
 * DEPENDENCIES:
 *   - Card component from UI library
 *   - TypeScript types (YahooFinanceTestProps)
 *
 * RISKS:
 *   - WARN: Yahoo Finance API calls may hit rate limits
 *   - NOTE: Component displays large JSON structures - may impact performance
 *   - NOTE: Ticker validation is minimal - may need more robust checks
 */

import type { YahooFinanceTestProperties } from "../types";

import { type FC } from "react";

import { Card } from "@/components/ui";

/**
 * WHAT:
 *   Component for testing Yahoo Finance enrichment with ticker identifiers.
 *
 * WHY:
 *   Encapsulates Yahoo Finance test UI logic.
 *
 * @param root0 - Component properties
 *
 * @param root0.identifier - Ticker identifier to test
 *
 * @param root0.result - Test result data
 *
 * @param root0.loading - Loading state
 *
 * @param root0.onIdentifierChange - Handler for identifier changes
 *
 * @param root0.onTest - Handler for test button click
 *
 * @returns {JSX.Element} React component for Yahoo Finance testing
 *
 */
export const YahooFinanceTest: FC<YahooFinanceTestProperties> = ({
  identifier,
  loading,
  onIdentifierChange,
  onTest,
  result,
}) => {
  return (
    <Card
      className="bg-slate-700/30 border-slate-600/50 shadow-tracq-card mb-4"
      padding="lg"
      variant="default"
    >
      <h3 className="text-lg font-semibold mb-4">🔍 Yahoo Finance Enrichment Test</h3>
      <p className="text-sm text-slate-400 mb-4">
        Teste Yahoo Finance Enrichment direkt mit einem Ticker (z.B. AAPL, ISPA.DE, SWDA.L). Der
        Ticker muss korrekt sein - keine Fallbacks mehr.
      </p>

      <div className="space-y-4">
        <div className="flex gap-4 items-end">
          <div className="flex-1">
            <label className="block text-sm font-medium mb-2">Ticker</label>
            <input
              className="w-full px-3 py-2 bg-slate-800 border border-slate-600 rounded text-white"
              disabled={loading}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                const target = e.target as HTMLInputElement;
                const value = target.value;
                onIdentifierChange(value);
              }}
              onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                if (e.key === "Enter" && !loading) {
                  const trimmedIdentifier = identifier.trim();
                  if (trimmedIdentifier.length > 0) {
                    onTest();
                  }
                }
              }}
              placeholder="AAPL, ISPA.DE, SWDA.L"
              type="text"
              value={identifier}
            />
          </div>

          <button
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={loading || identifier.trim().length === 0}
            onClick={onTest}
          >
            {loading ? "Lädt..." : "Testen"}
          </button>
        </div>

        {result === null ? null : (
          <div className="mt-4 p-4 bg-slate-800/50 rounded border border-slate-600">
            <h4 className="font-semibold mb-2 flex items-center gap-2">
              {result.success ? (
                <>
                  <span className="text-green-500">✅</span>
                  <span>Erfolgreich</span>
                </>
              ) : (
                <>
                  <span className="text-red-500">❌</span>
                  <span>Fehler</span>
                </>
              )}
            </h4>

            {result.success ? (
              <div className="space-y-2 text-sm">
                <div>
                  <strong>ISIN:</strong> {result.data?.isin ?? "N/A"}
                </div>
                <div>
                  <strong>Ticker:</strong> {result.data?.ticker ?? "N/A"}
                </div>
                <div>
                  <strong>Name:</strong> {result.data?.security_name ?? "N/A"}
                </div>
                <div>
                  <strong>Asset Type:</strong> {result.data?.asset_type ?? "N/A"}
                </div>
                <div>
                  <strong>Sector:</strong> {result.data?.sector ?? "N/A"}
                </div>
                <div>
                  <strong>Industry:</strong> {result.data?.industry ?? "N/A"}
                </div>
                <div>
                  <strong>Country:</strong> {result.data?.country ?? "N/A"}
                </div>
                <div>
                  <strong>Region:</strong> {result.data?.region ?? "N/A"}
                </div>
              </div>
            ) : (
              <div className="text-red-400">
                <strong>Fehler:</strong> {result.error ?? "Unknown error"}
              </div>
            )}

            <details className="mt-4">
              <summary className="cursor-pointer text-sm text-gray-400 hover:text-gray-300">
                Raw JSON anzeigen
              </summary>
              <div className="mt-2 space-y-4">
                {/* Ergebnis JSON */}
                <div>
                  <h5 className="text-xs font-semibold text-gray-400 mb-1">Ergebnis JSON:</h5>
                  <pre className="p-2 bg-slate-900 rounded text-xs overflow-auto text-gray-300 max-h-96">
                    {JSON.stringify(result, null, 2)}
                  </pre>
                </div>

                {/* Yahoo Finance Raw JSON */}
                {result.raw_json === undefined ? null : (
                  <div>
                    <h5 className="text-xs font-semibold text-gray-400 mb-1">
                      Yahoo Finance Raw JSON:
                    </h5>
                    {result.raw_json.quoteSummary === undefined ? null : (
                      <div className="mb-2">
                        <h6 className="text-xs font-medium text-gray-500 mb-1">
                          quoteSummary API:
                        </h6>
                        <pre className="p-2 bg-slate-900 rounded text-xs overflow-auto text-gray-300 max-h-96">
                          {JSON.stringify(result.raw_json.quoteSummary, null, 2)}
                        </pre>
                      </div>
                    )}
                    {result.raw_json.chart === undefined ? null : (
                      <div>
                        <h6 className="text-xs font-medium text-gray-500 mb-1">chart API:</h6>
                        <pre className="p-2 bg-slate-900 rounded text-xs overflow-auto text-gray-300 max-h-96">
                          {JSON.stringify(result.raw_json.chart, null, 2)}
                        </pre>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </details>
          </div>
        )}
      </div>
    </Card>
  );
};
